<template>
  <LoadingComponent v-if="loading" size="small" />
  <template v-if="!loading && localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            ref="panel_control"
            v-model:search-value="searchValue"
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :count-filter="validFilter.length"
            :is-dirty="$isStateDirty('localDashboard.filters')"
            :need-save-locked="loading"
            @dashboard-changed="dashboardChanged"
            @export-file="handleExportFile"
            @clear-filters="clearFilters"
          >
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                :dashboard-type="localDashboard.type"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template v-if="false" #display-options-content>
              <DisplayOptionsContent
                v-model:display-options="localDashboard.display_options"
              />
            </template>
            <template #other-cta>
              <AddNewColumnComponent model="invoice" @add-column="addColumn" />
            </template>
          </DashboardPanelControl>
        </template>
        <template #subtitle>
          <div class="grid grid-cols-[auto_1fr] items-center gap-x-4">
            <template v-if="hasFilterDates">
              <DateRangeNavigator
                v-model:start="specific_day_start"
                v-model:end="specific_day_end"
                size="small"
                @dates-changed="rangeNavigatorChanged"
              />
              <div class="text-lg font-bold capitalize">
                {{ periodTitle }}
              </div>
            </template>
            <template v-if="outstandingAsOfFilter">
              <DateRangeNavigator
                v-model:start="outstandingAsOfDate"
                v-model:end="outstandingAsOfDate"
                size="small"
                @dates-changed="outstandingAsOfChanged"
              />
              <div class="text-lg font-bold capitalize">
                {{ outstandingAsOfTitle }}
              </div>
            </template>
          </div>
        </template>
      </DashboardHeader>
    </div>
    <div class="relative flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <div
        v-if="loading"
        class="absolute top-1/2 left-1/2 z-100 h-36 w-36 -translate-x-1/2 -translate-y-1/2 transform rounded-2xl bg-gray-200"
      >
        <LoadingComponent :display-label="false" />
      </div>
      <AlertPanel
        v-if="isStatusFilterIgnored"
        type="warning"
        compact
        :title="$t('attributes.invoicing_outstanding_as_of_status_ignored')"
      />
      <InvoicingTable
        v-model:columns="localDashboard.columns"
        v-model:sort="localDashboard.sort"
        v-model:require-save-dashboard="requireSaveDashboard"
        :invoices="filtered_invoices"
        :loading="loadingInvoicing"
        :linked-documents="!!localDashboard.display_options?.linked_documents"
        @update:linked-documents="setLinkedDocuments"
        @fetch-invoices="fetchInvoicing"
        @update:dashboard="updateDashboard"
      />
    </div>
  </template>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import AlertPanel from '@ui/AlertPanel.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import InvoicingTable from '@c/Dashboard/Content/Invoicing/InvoicingTable.vue'
import DashboardPanelControl from '../../Components/DashboardPanelControl.vue'
import DisplayOptionsContent from '@c/Dashboard/Content/Invoicing/Panel/DisplayOptionsContent.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import AddNewColumnComponent from '@c/Dashboard/Content/Invoicing/components/Head/columnSettings/AddNewColumnComponent.vue'
import DocTypes from '../../../../assets/enums/invoicing/DocTypes'
import DateRangeNavigator from '@c/SelectComponent/DateRangeNavigator.vue'
import {
  rowMatchesSearch,
  searchableKeys
} from '@c/Dashboard/Components/dashboardSearch'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin'

const OUTSTANDING_AS_OF_COLUMN = 'outstanding_as_of'
const WARNING_STATUS_FILTER_IGNORED = 'outstanding_as_of_status_filter_ignored'

export default {
  name: 'InvoicingContent',
  components: {
    AlertPanel,
    DateRangeNavigator,
    AddNewColumnComponent,
    DisplayOptionsContent,
    FilterLinesComponent,
    DashboardPanelControl,
    InvoicingTable,
    DashboardHeader,
    LoadingComponent
  },
  mixins: [persistViewFiltersStateMixin],
  persistViewState: [
    'searchValue',
    'specific_day_enabled',
    'specific_day_start',
    'specific_day_end',
    'localDashboard.filters'
  ],
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingInvoicing: false,
      searchValue: '',
      specific_day_enabled: false,
      specific_day_start: null,
      specific_day_end: null,
      invoicesList: [],
      warnings: [],
      localDashboard: null,
      requireSaveDashboard: false
    }
  },
  computed: {
    // The API only computes what the dashboard displays: it reads it from the request
    listDisplayOptions() {
      return {
        linked_documents:
          !!this.localDashboard?.display_options?.linked_documents
      }
    },
    ...mapGetters({
      metadata: 'metadata/getInitialMetadata',
      availableFilter: 'metadata/getAvailableInvoiceDossier'
    }),
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    },
    filtrableColumns() {
      return searchableKeys(this.localDashboard.columns)
    },
    filtered_invoices() {
      let tmpInvoiceList = [...this.invoicesList]
      if (this.searchValue.length >= 2) {
        const searchValue = this.$h.normalizeSearch(this.searchValue)
        tmpInvoiceList = tmpInvoiceList.filter((model) =>
          rowMatchesSearch(model, this.filtrableColumns, searchValue, (value) =>
            this.$h.normalizeSearch(value)
          )
        )
      }
      if (this.localDashboard.sort?.column_id) {
        let sortRealColumn = this.localDashboard.columns?.find(
          (column) => column.id === this.localDashboard.sort.column_id
        )
        let sortRealColumnValue =
          sortRealColumn?.sort?.value ?? sortRealColumn?.field

        if (sortRealColumnValue) {
          tmpInvoiceList.sort((a, b) => {
            const direction =
              this.localDashboard.sort.direction === 'desc' ? -1 : 1

            let aValue = a[sortRealColumnValue] ?? ''
            let bValue = b[sortRealColumnValue] ?? ''

            if (sortRealColumn.type === 'currency') {
              aValue = parseFloat(aValue ?? 0)
              bValue = parseFloat(bValue ?? 0)
            }

            let result =
              typeof aValue === 'number'
                ? aValue - bValue
                : aValue.toString().localeCompare(bValue.toString())

            if (result === 0) {
              const referenceField = this.localDashboard.columns[0]?.field
              if (referenceField) {
                const refA = a[referenceField]?.toString() ?? ''
                const refB = b[referenceField]?.toString() ?? ''
                result = refA.localeCompare(refB)
              }
            }

            return result * direction
          })
        }
      }
      return tmpInvoiceList
    },
    hasFilterDates() {
      return this.localDashboard.filters
        ?.map((filter) => filter.column)
        ?.some((column) =>
          ['date_specific', 'date_relative', 'date_range'].includes(column)
        )
    },
    isStatusFilterIgnored() {
      return this.warnings.includes(WARNING_STATUS_FILTER_IGNORED)
    },
    outstandingAsOfFilter() {
      return this.localDashboard.filters?.find(
        (filter) => filter.column === OUTSTANDING_AS_OF_COLUMN && filter.values
      )
    },
    outstandingAsOfDate: {
      get() {
        return this.outstandingAsOfFilter?.values
      },
      set(value) {
        if (this.outstandingAsOfFilter) {
          this.outstandingAsOfFilter.values = value
        }
      }
    },
    outstandingAsOfTitle() {
      const label = this.availableFilter.find(
        (column) => column.id === OUTSTANDING_AS_OF_COLUMN
      )?.name

      return `${label ?? ''} ${this.$h.getDateRangeString(
        this.outstandingAsOfDate,
        this.outstandingAsOfDate,
        'll'
      )}`.trim()
    },
    periodTitle() {
      if (
        this.specific_day_enabled &&
        this.specific_day_start &&
        this.specific_day_end
      ) {
        return this.$h.getDateRangeString(
          this.specific_day_start,
          this.specific_day_end,
          'ddd ll'
        )
      }

      const filters =
        this.localDashboard.filters?.filter((filter) =>
          ['date_range', 'date_specific', 'date_relative'].includes(
            filter.column
          )
        ) ?? []

      if (filters.length && filters[0].values) {
        const { column, values } = filters[0]

        if (column === 'date_range' && values?.start && values?.end) {
          return this.$h.getDateRangeString(values.start, values.end, 'ddd ll')
        } else if (column === 'date_relative' && values) {
          try {
            if (typeof values === 'string' && values.trim() !== '') {
              return `${this.$t(
                `enums.common.relativeDates.${values}`
              )} - ${this.$h.getRelativeDateString(values, 'ddd ll')}`
            } else {
              return ''
            }
          } catch (error) {
            return ''
          }
        } else if (column === 'date_specific' && values) {
          return this.$h.getDateRangeString(values, values, 'ddd ll')
        }
      }

      return '-'
    }
  },
  watch: {
    currentBoard(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.localDashboard = newValue
        this.fetchTemplateDashboardConfig()
      }
    },
    '$route.meta.doc_type'(newValue) {
      if (!newValue) return
      this.invoicesList = []
      this.fetchInvoicing()
      if (this.requireSaveDashboard) {
        this.requireSaveDashboard = false
      }
    },
    'localDashboard.filters': {
      handler(newValue) {
        this.filterUpdated()
      },
      deep: true
    }
  },
  mounted() {
    this.fetchTemplateDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchListInvoicingAction: 'dashboard/fetchListInvoicingDashboard',
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processInvoiceExportExcelAction: 'invoicing/invoiceExportExcel'
    }),
    clearFilters() {
      this.localDashboard.filters = this.$getInitialState(
        'localDashboard.filters'
      )
      this.fetchInvoicing()
    },
    fetchTemplateDashboardConfig() {
      this.loading = true
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
          this.$restoreCachedState(
            'localDashboard.filters',
            response.template_dashboard.filters
          )
          this.fetchInvoicing()
        })
        .catch((error) => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
        .finally((_) => {
          if (this.currentBoard.id === targetId) this.loading = false
        })
    },
    updateDashboard(reloadDossier) {
      this.$refs.panel_control.saveDashboard(() => {
        if (reloadDossier) {
          this.fetchInvoicing()
        }
      })
    },
    fetchInvoicing() {
      let doc_type = null
      switch (this.localDashboard.type) {
        case 'invoice':
          doc_type = DocTypes.INVOICE
          break
        case 'purchase_order':
          doc_type = DocTypes.PURCHASE_ORDER
          break
        case 'quote':
          doc_type = DocTypes.QUOTE
          break
        case 'credit_note':
          doc_type = DocTypes.CREDIT_NOTE
          break
        case 'invoice_proforma':
          doc_type = DocTypes.INVOICE_PROFORMA
          break
      }
      this.invoicesList = []
      this.warnings = []
      this.loadingInvoicing = true
      this.processFetchListInvoicingAction({
        doc_type: doc_type,
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        display_options: this.listDisplayOptions,
        template_id: this.localDashboard.id
      })
        .then((response) => {
          this.invoicesList = response.invoices
          this.warnings = response.warnings ?? []
          this.filterUpdated()
        })
        .catch(() => {})
        .finally((_) => (this.loadingInvoicing = false))
    },
    fetchInvoicesFromShortDates() {
      let doc_type = null

      switch (this.localDashboard.type) {
        case 'invoice':
          doc_type = DocTypes.INVOICE
          break
        case 'purchase_order':
          doc_type = DocTypes.PURCHASE_ORDER
          break
        case 'quote':
          doc_type = DocTypes.QUOTE
          break
        case 'credit_note':
          doc_type = DocTypes.CREDIT_NOTE
          break
        case 'invoice_proforma':
          doc_type = DocTypes.INVOICE_PROFORMA
          break
      }

      if (
        this.specific_day_enabled &&
        this.specific_day_start &&
        this.specific_day_end
      ) {
        const values = {
          start: this.specific_day_start,
          end: this.specific_day_end
        }

        const filters = (this.localDashboard.filters || []).map((filter) => {
          if (
            ['date_specific', 'date_range', 'date_relative'].includes(
              filter.column
            )
          ) {
            return {
              ...filter,
              column: 'date_range',
              values: values,
              whereType: filter.whereType ?? 'where'
            }
          }
          return filter
        })

        this.warnings = []
        this.processFetchListInvoicingAction({
          doc_type: doc_type,
          filters: filters,
          columns: this.localDashboard.columns,
          display_options: this.listDisplayOptions,
          template_id: this.localDashboard.id
        })
          .then((response) => {
            this.invoicesList = response.invoices
            this.warnings = response.warnings ?? []
          })
          .catch(() => {})
      }
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchInvoicing()
    },
    dashboardChanged(dashboard_template_id) {},
    addColumn(config) {
      this.localDashboard.columns.push({
        ...config,
        sum_able: ['currency'].includes(config.type?.toLowerCase()),
        template_id: this.localDashboard.id
      })
    },
    setLinkedDocuments(value) {
      this.localDashboard.display_options = {
        ...this.localDashboard.display_options,
        linked_documents: value
      }
      // Like the other options: shown (or hidden) right away, kept only once the
      // user saves the dashboard with the green button
      this.requireSaveDashboard = true
      this.fetchInvoicing()
    },
    configUpdated() {
      console.log('save config')
    },
    handleExportFile() {
      this.processInvoiceExportExcelAction({
        doc_type: this.$route.meta.doc_type,
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        template_id: this.localDashboard.id,
        sort: this.localDashboard.sort,
        terms: this.searchValue
      }).then((response) => {
        window.open(response.url)
      })
    },
    filterUpdated() {
      let filters =
        this.localDashboard.filters.filter((filter) =>
          ['date_range', 'date_specific', 'date_relative'].includes(
            filter.column
          )
        ) ?? []
      if (filters.length && filters[0].values !== null) {
        if (filters[0].column === 'date_range') {
          this.specific_day_start = filters[0].values.start
          this.specific_day_end = filters[0].values.end
        } else if (filters[0].column === 'date_relative') {
          let relativeDates = this.$h.getRelativeDateAsArray(filters[0].values)
          if (Array.isArray(relativeDates) && relativeDates.length === 2) {
            this.specific_day_start = relativeDates[0].format('YYYY-MM-DD')
            this.specific_day_end = relativeDates[1].format('YYYY-MM-DD')
          }
        } else if (filters[0].column === 'date_specific') {
          this.specific_day_start = filters[0].values
          this.specific_day_end = filters[0].values
        }
      }
    },
    outstandingAsOfChanged() {
      if (this.specific_day_enabled) {
        this.fetchInvoicesFromShortDates()
      } else {
        this.fetchInvoicing()
      }
    },
    rangeNavigatorChanged() {
      this.specific_day_enabled = true
      this.fetchInvoicesFromShortDates()
    }
  }
}
</script>

<style scoped></style>
